import { apiClient } from './apiClient';
import type {
  AnalyticsOverview,
  AuthResponse,
  BoostPackage,
  CalendarPayload,
  OwnedVenue,
  PromotionRow,
  TimeseriesPoint,
  User,
} from './types';

export const authApi = {
  async login(email: string, password: string): Promise<AuthResponse> {
    const { data } = await apiClient.post<AuthResponse>('/auth/login', {
      email,
      password,
      device_name: 'web-dashboard',
    });
    return data;
  },
  async me(): Promise<{ user: User }> {
    const { data } = await apiClient.get<{ user: User }>('/auth/me');
    return data;
  },
  async logout(): Promise<void> {
    await apiClient.post('/auth/logout').catch(() => {});
  },
};

export const venuesApi = {
  async owned(): Promise<OwnedVenue[]> {
    // Hits an admin-friendly list — we filter venues claimed_by_user_id = me on the backend
    // by reusing the existing per-city venues listing; quick way: ask /auth/me + read venues.
    // For dashboard simplicity, fall back to user.ownedVenues via /auth/export until a dedicated
    // endpoint exists. Below is a thin shim that pulls all claimed venues for the user.
    const { data } = await apiClient.get<{ profile: any; owned_venues: any[] }>('/auth/export');
    return (data.owned_venues ?? []).map((v: any) => ({
      id: v.id,
      name: v.name,
      slug: v.slug ?? '',
      city_id: v.city_id ?? 0,
      neighborhood: v.neighborhood ?? null,
    }));
  },
};

export const boostApi = {
  async packages(cityId?: number): Promise<BoostPackage[]> {
    const { data } = await apiClient.get<{ packages: BoostPackage[] }>('/boost/packages', {
      params: { city_id: cityId },
    });
    return (data.packages ?? []) as BoostPackage[];
  },
  async overview(): Promise<AnalyticsOverview> {
    const { data } = await apiClient.get<AnalyticsOverview>('/boost/analytics/overview');
    return data;
  },
  async timeseries(days = 30): Promise<TimeseriesPoint[]> {
    const { data } = await apiClient.get<{ series: TimeseriesPoint[] }>(
      '/boost/analytics/timeseries',
      { params: { days } },
    );
    return data.series;
  },
  async byPromotion(): Promise<PromotionRow[]> {
    const { data } = await apiClient.get<{ promotions: PromotionRow[] }>(
      '/boost/analytics/by-promotion',
    );
    return data.promotions;
  },
  async calendar(cityId: number, slotTypeCode: string, days = 14): Promise<CalendarPayload> {
    const { data } = await apiClient.get<CalendarPayload>('/boost/calendar', {
      params: { city_id: cityId, slot_type_code: slotTypeCode, days },
    });
    return data;
  },
  async cancel(promotionId: number) {
    const { data } = await apiClient.post(`/boost/${promotionId}/cancel`);
    return data;
  },
};
